import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './ScannerDashboard.css';
import { isB3TradingDay, isB3MarketOpen } from '../utils/b3Calendar';
import { getKairi, getReversalMonitor, getRSI2, getScanner, getSetorial, getStrategyHealth } from '../api/brapi';
import WebSocketManager from './WebSocketManager';
import PortfolioModal from './PortfolioModal';
import DismissibleNotice from './DismissibleNotice';
import { addPosition } from '../api/portfolio';

const SECTOR_EMOJI = {
  Bancos: '🏦', Petróleo: '🛢', Mineração: '⛏', Varejo: '🛒',
  Consumo: '🥩', Industrial: '⚙', Energia: '⚡', Saúde: '💊',
  Imobiliário: '🏗', ETF: '📊',
};

const SECTOR_COLOR = {
  Bancos: '#3b82f6', Petróleo: '#f59e0b', Mineração: '#94a3b8',
  Varejo: '#ec4899', Consumo: '#f97316', Industrial: '#a78bfa',
  Energia: '#facc15', Saúde: '#34d399', Imobiliário: '#60a5fa', ETF: '#818cf8',
};

const TIER_MAP = {
  ITUB4: 'S', BBDC4: 'S', SANB11: 'S', BBAS3: 'S',
  VALE3: 'S', CSNA3: 'S', GGBR4: 'S', USIM5: 'S',
  PETR4: 'A', PETR3: 'A', PRIO3: 'A', RECV3: 'A',
  ELET3: 'A', ELET6: 'A', ENGI11: 'A', EQTL3: 'A', CMIG4: 'A', CPLE6: 'A',
  ABEV3: 'A', JBSS3: 'A', BRFS3: 'A', MDIA3: 'A',
  WEGE3: 'A', EMBR3: 'A', RAIL3: 'A', VBBR3: 'A',
  LREN3: 'B', ASAI3: 'B', RADL3: 'B', ARZZ3: 'B', GRND3: 'B',
  HAPV3: 'B', RDOR3: 'B', FLRY3: 'B', DASA3: 'B',
  CYRE3: 'B', EZTC3: 'B', MRVE3: 'B', TEND3: 'B',
  VIVT3: 'B', TIMS3: 'B',
  MGLU3: 'C', AMER3: 'C', CASH3: 'C', TOTS3: 'C',
  AZUL4: 'C', GOLL4: 'C',
  BOVA11: 'ETF', SMAL11: 'ETF', IVVB11: 'ETF', FIND11: 'ETF', MATB11: 'ETF',
};

const TIER_META = {
  S:   { label: 'Tier S', desc: 'Excelentes para mean reversion',  cls: 'tier-s' },
  A:   { label: 'Tier A', desc: 'Muito bons, operar ativamente',   cls: 'tier-a' },
  B:   { label: 'Tier B', desc: 'Funcionam com ressalvas',         cls: 'tier-b' },
  C:   { label: 'Tier C', desc: 'Evitar — risco idiossincrático',  cls: 'tier-c' },
  ETF: { label: 'ETFs',   desc: 'Ideal para iniciantes',           cls: 'tier-etf' },
};
const EMPTY_TICKERS = Object.freeze([]);

const FILTERS = [
  { key: 'all',    label: 'Todos' },
  { key: 'entry',  label: 'Entradas' },
  { key: 'watch',  label: 'Acompanhar' },
  { key: 'risk',   label: 'Cuidado / saída' },
];

const SORTS = [
  { key: 'priority', label: 'Prioridade' },
  { key: 'score',    label: 'Score' },
  { key: 'ticker',   label: 'Ativo' },
  { key: 'strategy', label: 'Origem' },
];

function fmtZ(v)          { if (v == null) return '—'; return `${v > 0 ? '+' : ''}${v.toFixed(2)}σ`; }
function fmtPct(v)        { if (v == null) return '—'; return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`; }
function fmtN(v, dec = 1) { if (v == null) return '—'; return v.toFixed(dec); }
function fmtPrice(v)      { return v == null ? '—' : `R$ ${Number(v).toFixed(2).replace('.', ',')}`; }
function fmtBeta(v)       { return Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '—'; }

const STRATEGIES = [
  { key: 'scanner',   label: 'Scanner B3',              tone: 'scanner' },
  { key: 'reversal',  label: 'Reversão na Tendência',   tone: 'reversal' },
  { key: 'rsi2',      label: 'RSI(2) Connors',          tone: 'rsi2' },
  { key: 'confluence',label: 'Kairi + Confluência',     tone: 'confluence' },
  { key: 'setorial',  label: 'Análise Setorial',        tone: 'setorial' },
];

const KAI_PARAMS = {
  buyThreshold: -5,
  sellThreshold: 5,
  rsiThreshold: 10,
  ibsThreshold: 0.2,
  volThreshold: 1,
};

function getConfluence(asset) {
  const kairi = Number(asset.kairi);
  const rsi2 = Number(asset.rsi2);
  const ibs = Number(asset.ibs);
  const trendPct = Number(asset.trendPct);
  const volRatio = Number.isFinite(Number(asset.volRatio)) ? Number(asset.volRatio) : 1;
  const volumePass = volRatio >= KAI_PARAMS.volThreshold;
  const volumeWarn = !volumePass && volRatio >= 0.7;
  const checks = [
    kairi <= KAI_PARAMS.buyThreshold,
    rsi2 <= KAI_PARAMS.rsiThreshold,
    ibs <= KAI_PARAMS.ibsThreshold,
    trendPct > 0,
    volumePass,
  ];
  const score = checks.reduce((total, passed) => total + (passed ? 20 : 0), 0) + (volumeWarn ? 10 : 0);
  return { score, filtered: !(trendPct > 0) };
}

function classifyConfluence(asset, conf) {
  if (conf.filtered) return 'filtered';
  if (asset.kairi >= KAI_PARAMS.sellThreshold) return 'sell';
  if (conf.score >= 75 && asset.kairi <= KAI_PARAMS.buyThreshold) return 'buy';
  if (conf.score >= 60 && asset.kairi <= KAI_PARAMS.buyThreshold + 1) return 'watch';
  if (conf.score >= 40) return 'watch';
  return 'skip';
}

function connorsScore(asset) {
  return [
    asset.r1AboveMa200,
    asset.r2BelowMa5,
    asset.r3Approaching,
    asset.r4Entry,
  ].filter(Boolean).length * 25;
}

function normalizedStatus(status) {
  const map = {
    buy: ['Compra', 'buy', 'entry'],
    investigate: ['Investigar', 'investigate', 'watch'],
    danger: ['Cuidado', 'danger', 'risk'],
    watch: ['Acompanhar', 'watch', 'watch'],
    sell: ['Saída', 'danger', 'risk'],
    approaching: ['Próximo', 'investigate', 'watch'],
    ARMADO: ['Armado', 'investigate', 'entry'],
    DISPARADO: ['Disparado', 'buy', 'entry'],
    review: ['Revisar data ex', 'investigate', 'watch'],
  };
  const [label, tone, group] = map[status] || ['—', 'skip', 'watch'];
  return { label, tone, group };
}

const SCANNER_STATUS_ORDER = { DISPARADO: 0, ARMADO: 1 };
function scannerStatusRank(row) {
  const raw = String(row?.status || '').toUpperCase();
  return SCANNER_STATUS_ORDER[raw] ?? 2;
}

function KpiCard({ label, value, sub, color }) {
  return (
    <div className="sc-kpi">
      <div className="sc-kpi-label">{label}</div>
      <div className={`sc-kpi-value ${color || ''}`}>{value}</div>
      {sub && <div className="sc-kpi-sub">{sub}</div>}
    </div>
  );
}

export default function ScannerDashboard({ onAssetClick, onNewAlerts, universeTickers = EMPTY_TICKERS }) {
  const [data,       setData]      = useState(null);
  const [sourceData, setSourceData] = useState({ kairi: null, reversal: null, rsi2: null, setorial: null });
  const [strategyHealth, setStrategyHealth] = useState(null);
  const [loading,    setLoading]   = useState(true);
  const [error,      setError]     = useState(null);
  const [filter,     setFilter]    = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [tierFilter, setTierFilter]= useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortKey,    setSortKey]   = useState('priority');
  const [sortAsc,    setSortAsc]   = useState(true);
  const [newAlerts,  setNewAlerts] = useState([]);
  const [liveData,   setLiveData]  = useState({});
  const [flashTickers, setFlashTickers] = useState(new Set());
  const [flashDir,     setFlashDir]     = useState({});   // { ticker: 'up'|'down' }
  const [portfolioAsset, setPortfolioAsset] = useState(null);

  const prevSignalsRef  = useRef(null);
  const initializedRef  = useRef(false);
  const wsmRef          = useRef(null);

  // ── helper: flash tickers with direction ─────────────────────────────────
  const flashUpdated = useCallback((entries) => {
    // entries: [{ ticker, dir }]  or legacy string array
    if (!entries || !entries.length) return;
    const normalized = entries.map(e =>
      typeof e === 'string' ? { ticker: e, dir: 'up' } : e
    );
    const tickers = normalized.map(e => e.ticker);
    const dirMap  = Object.fromEntries(normalized.map(e => [e.ticker, e.dir]));
    setFlashDir(prev => ({ ...prev, ...dirMap }));
    setFlashTickers(prev => new Set([...prev, ...tickers]));
    setTimeout(() => {
      setFlashTickers(prev => {
        const n = new Set(prev); tickers.forEach(t => n.delete(t)); return n;
      });
      setFlashDir(prev => {
        const n = { ...prev }; tickers.forEach(t => delete n[t]); return n;
      });
    }, 700);
  }, []);

  // ── Load each strategy independently, preserving its own calculation rules. ─
  const load = useCallback(async () => {
    if (!initializedRef.current) { setLoading(true); setError(null); }
    const ensureRenderable = () => setData(previous => previous || {
      assets: [], kpis: { total: 0, aboveMm200: 0, buy: 0, investigate: 0, filtered: 0 }, sectorZ: {},
    });
    const recordFailure = (key) => {
      ensureRenderable();
    };
    const publishScanner = (scanner) => {
      setData(scanner);
      if (!initializedRef.current) {
        setLiveData(previous => {
          const next = { ...previous };
          (scanner.assets || []).forEach(asset => {
            if (asset.close != null && !next[asset.ticker]) {
              next[asset.ticker] = { price: asset.close, change: null, source: 'db' };
            }
          });
          return next;
        });
      }

      const previousSignals = prevSignalsRef.current;
      if (previousSignals !== null && scanner.assets) {
        const alerts = [];
        scanner.assets.forEach(asset => {
          const oldSignal = previousSignals.get(asset.ticker);
          if (asset.signal === oldSignal) return;
          if (asset.signal === 'buy') alerts.push({ ticker: asset.ticker, type: 'buy', label: 'Compra' });
          else if (asset.signal === 'danger') alerts.push({ ticker: asset.ticker, type: 'sell', label: 'Cuidado' });
          else if (asset.signal === 'investigate' && oldSignal !== 'buy') {
            alerts.push({ ticker: asset.ticker, type: 'investigate', label: 'Investigar' });
          }
        });
        if (alerts.length && isB3TradingDay()) {
          const eventTime = Date.now();
          setNewAlerts(alerts.map((alert, index) => ({ ...alert, eventKey: `${eventTime}:${index}` })));
          setTimeout(() => setNewAlerts([]), 8000);
          onNewAlerts?.(alerts);
        }
      }
      prevSignalsRef.current = new Map((scanner.assets || []).map(asset => [asset.ticker, asset.signal]));
      initializedRef.current = true;
    };
    const publishSource = (key, value) => {
      ensureRenderable();
      setSourceData(previous => ({ ...previous, [key]: value }));
    };

    const requests = [
      getScanner().then(publishScanner).catch(() => recordFailure('scanner')),
      getReversalMonitor().then(value => publishSource('reversal', value)).catch(() => recordFailure('reversal')),
      getRSI2(universeTickers.join(',')).then(value => publishSource('rsi2', value)).catch(() => recordFailure('rsi2')),
      getKairi(universeTickers.join(',')).then(value => publishSource('kairi', value)).catch(() => recordFailure('confluence')),
      getSetorial().then(value => publishSource('setorial', value)).catch(() => recordFailure('setorial')),
      getStrategyHealth().then(setStrategyHealth).catch(() => setStrategyHealth(null)),
    ];

    await Promise.all(requests);
    setLoading(false);
  }, [onNewAlerts, universeTickers]);

  // Strategy data refreshes every five minutes; live prices still update below.
  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);


  // ── Silent price poll every 10 s via HTTP (WebSocket fallback) ───────────
  useEffect(() => {
    let active = true;
    async function pollPrices() {
      try {
        const r = await fetch('/api/prices/live');
        if (!r.ok || !active) return;
        const payload = await r.json();
        const prices = Array.isArray(payload) ? payload : (payload.prices || []);
        const entries = [];
        setLiveData(prev => {
          const next = { ...prev };
          prices.forEach(({ ticker, price, change }) => {
            if (price == null) return;
            const old = prev[ticker];
            if (!old || old.price !== price) {
              const dir = old ? (price > old.price ? 'up' : 'down') : 'up';
              next[ticker] = { price, change: change ?? old?.change ?? null, source: 'poll' };
              if (old) entries.push({ ticker, dir });
            }
          });
          setTimeout(() => flashUpdated(entries), 0);
          return next;
        });
      } catch (_) {}
    }
    pollPrices();
    // Keep quotes fresh during the B3 session without turning this into a
    // second strategy refresh loop. The initial snapshot above is intentional.
    const id = setInterval(() => {
      if (isB3MarketOpen()) pollPrices();
    }, 15 * 60 * 1000);
    return () => { active = false; clearInterval(id); };
  }, [flashUpdated]);

  // WebSocket — cotação ao vivo
  const handleWsMessage = useCallback((priceData) => {
    const updated = [];
    setLiveData(prev => {
      const next = { ...prev };
      priceData.forEach(({ ticker, value, variation }) => {
        const t = ticker.replace('.SA', '');
        next[t] = { price: parseFloat(value), change: parseFloat(variation) };
        updated.push(t);
      });
      return next;
    });
    if (updated.length > 0) {
      setFlashTickers(prev => new Set([...prev, ...updated]));
      setTimeout(() => {
        setFlashTickers(prev => {
          const next = new Set(prev);
          updated.forEach(t => next.delete(t));
          return next;
        });
      }, 600);
    }
  }, []);

  useEffect(() => {
    const wsm = new WebSocketManager(handleWsMessage, () => {});
    wsm.connect();
    wsmRef.current = wsm;
    return () => wsm.close();
  }, [handleWsMessage]);

  // Subscrever tickers quando os dados carregarem
  useEffect(() => {
    if (!data || !wsmRef.current) return;
    const symbols = data.assets.map(a => `${a.ticker}|S`);
    wsmRef.current.subscribeToSymbols(symbols);
  }, [data]);

  const consolidatedRows = useMemo(() => {
    const rows = [];
    const scannerRiskByTicker = new Map(
      (data?.assets || []).map(asset => [asset.ticker, {
        hasOptions: asset.hasOptions,
        betaSector: asset.betaSector,
        betaIbovespa: asset.betaIbovespa,
        tier: asset.tier || TIER_MAP[asset.ticker] || null,
      }])
    );
    const add = row => {
      const risk = scannerRiskByTicker.get(row.ticker) || {};
      const tier = row.tier || risk.tier || TIER_MAP[row.ticker] || null;
      const tierHealthKey = row.strategyKey === 'rsi2'
        ? (tier === 'SC' ? 'connorsSc' : 'connorsBlue')
        : row.strategyKey === 'confluence'
        ? (tier === 'SC' ? 'confluenceSc' : 'confluenceBlue')
        : row.strategyKey;
      const strategyHealthEntry = strategyHealth?.strategies?.[tierHealthKey]
        || strategyHealth?.strategies?.[row.strategyKey];
      const healthGatedStrategy = ['rsi2', 'confluence', 'setorial', 'reversal'].includes(row.strategyKey);
      const riskRewardBlocked = row.strategyKey === 'reversal'
        && Number.isFinite(Number(row.riskReward))
        && Number(row.riskReward) < 1;
      const healthBlocked = healthGatedStrategy && strategyHealthEntry?.blocked !== false;
      const rowBlocks = Array.isArray(row.blocks) ? row.blocks : [];
      rows.push({
        id: `${row.strategyKey}-${row.ticker}-${row.status}`,
        ...row,
        hasOptions: row.hasOptions ?? risk.hasOptions ?? null,
        betaSector: row.betaSector ?? risk.betaSector ?? null,
        betaIbovespa: row.betaIbovespa ?? risk.betaIbovespa ?? null,
        tier,
        riskRewardBlocked,
        strategyHealth: strategyHealthEntry,
        requiresOverride: healthBlocked,
        entryBlocked: healthBlocked || riskRewardBlocked || rowBlocks.length > 0,
        entryBlockTitle: riskRewardBlocked
          ? 'R/R < 1 — não operar'
          : rowBlocks.length > 0
          ? rowBlocks.join(' · ')
          : strategyHealthEntry?.available === false
          ? 'Saúde da estratégia indisponível — entrada bloqueada'
          : 'Estratégia com expectativa negativa — bloqueada',
      });
    };
    const sectorByTicker = new Map();
    (data?.assets || []).forEach(asset => sectorByTicker.set(asset.ticker, asset.sector));
    (sourceData.setorial?.allAssets || []).forEach(asset => {
      if (asset.sectorName) sectorByTicker.set(asset.ticker, asset.sectorName);
    });
    const kairiReferenceDate = sourceData.kairi?.referenceDate || null;
    const rsi2ReferenceDate = sourceData.rsi2?.referenceDate || null;
    const dailySourcesAligned = !kairiReferenceDate
      || !rsi2ReferenceDate
      || kairiReferenceDate === rsi2ReferenceDate;
    const kairiByTicker = new Map(
      (sourceData.kairi?.assets || []).map(asset => [asset.ticker, asset]),
    );
    const rsi2ByTicker = new Map(
      (sourceData.rsi2?.assets || []).map(asset => [asset.ticker, asset]),
    );

    (data?.assets || [])
      .filter(asset => asset.signal && asset.signal !== 'skip')
      .forEach(asset => {
        const status = normalizedStatus(asset.signal);
        add({
          ticker: asset.ticker, name: asset.name, sector: asset.sector, price: asset.close,
          strategyKey: 'scanner', strategy: 'Scanner B3', status: asset.signal,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score: null, context: `Z-Kairi ${fmtZ(asset.zKairi)} · RSI(2) ${fmtN(asset.rsi2)}`,
          hasOptions: asset.hasOptions, betaSector: asset.betaSector, betaIbovespa: asset.betaIbovespa,
          needsReview: asset.needsReview === true,
          portfolio: { refs: [{ label: 'Z-Kairi', value: fmtZ(asset.zKairi), highlight: true }, { label: 'RSI(2)', value: fmtN(asset.rsi2) }] },
          priority: asset.signal === 'buy' ? 0 : asset.signal === 'investigate' ? 1 : asset.signal === 'watch' ? 2 : 3,
        });
      });

    [...(sourceData.reversal?.triggered || []), ...(sourceData.reversal?.armed || [])]
      .forEach(asset => {
        const status = normalizedStatus(asset.status);
        add({
          ticker: asset.ticker, name: asset.name, sector: asset.sector, price: asset.close,
          strategyKey: 'reversal', strategy: 'Reversão na Tendência', status: asset.status,
          riskReward: asset.riskReward,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score: asset.score, scoreMax: asset.scoreMax, conditions: asset.conditions, blocks: asset.blocks,
          needsReview: asset.needsReview === true,
          context: `Score ${asset.score ?? '—'}/${asset.scoreMax ?? 100} · RSI(2) ${fmtN(asset.rsi2)} · R/R ${asset.riskReward != null ? `${fmtN(asset.riskReward, 2)}R` : '—'}`,
          portfolio: {
            refs: [{ label: 'RSI(2)', value: fmtN(asset.rsi2), highlight: true }, { label: 'Risco / retorno', value: asset.riskReward != null ? `${fmtN(asset.riskReward, 2)}R` : '—' }],
            entryPrice: asset.entryPrice ?? asset.close, stop: asset.stopPrice, target: asset.target1Price,
          },
          priority: asset.status === 'DISPARADO' ? 0 : 1,
        });
      });

    (sourceData.rsi2?.assets || [])
      .filter(asset => asset.status && asset.status !== 'excluded')
      .forEach(asset => {
        const status = normalizedStatus(asset.status);
        const score = connorsScore(asset);
        const matchingKairi = dailySourcesAligned ? kairiByTicker.get(asset.ticker) : null;
        const kairiContext = matchingKairi ? `Kairi ${fmtPct(matchingKairi.kairi)} · ` : '';
        add({
          ticker: asset.ticker, name: asset.name, sector: sectorByTicker.get(asset.ticker) || 'Não informado', price: asset.price,
          strategyKey: 'rsi2', strategy: 'RSI(2) Connors', status: asset.status,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score, context: `${kairiContext}RSI(2) ${fmtN(asset.rsi2)} · ${asset.rulesCount ?? score / 25}/4 regras`,
          portfolio: {
            refs: [
              { label: 'Score Connors', value: `${score}%`, highlight: true },
              ...(matchingKairi ? [{ label: 'Kairi', value: fmtPct(matchingKairi.kairi) }] : []),
              { label: 'RSI(2)', value: fmtN(asset.rsi2) },
            ],
            entryPrice: asset.price, stop: asset.stopPrice, target: asset.exitTarget,
          },
          priority: asset.status === 'buy' ? 0 : asset.status === 'approaching' ? 2 : 3,
        });
      });

    (dailySourcesAligned ? sourceData.kairi?.assets || [] : [])
      .forEach(sourceAsset => {
        const matchingRsi2 = rsi2ByTicker.get(sourceAsset.ticker);
        const asset = matchingRsi2
          ? { ...sourceAsset, rsi2: matchingRsi2.rsi2 }
          : sourceAsset;
        const conf = getConfluence(asset);
        const result = classifyConfluence(asset, conf);
        if (result === 'skip' || result === 'filtered') return;
        const status = normalizedStatus(result);
        add({
          ticker: asset.ticker, name: asset.name, sector: asset.sector, price: asset.price,
          strategyKey: 'confluence', strategy: 'Kairi + Confluência', status: result,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score: conf.score, context: `Kairi ${fmtPct(asset.kairi)} · RSI(2) ${fmtN(asset.rsi2)}`,
          portfolio: {
            refs: [
              { label: 'Score de confluência', value: `${conf.score}%`, highlight: true },
              { label: 'Kairi', value: fmtPct(asset.kairi) },
              { label: 'RSI(2)', value: fmtN(asset.rsi2) },
            ],
            entryPrice: asset.price,
            signalScore: conf.score,
          },
          priority: result === 'buy' ? 0 : result === 'watch' ? 2 : 4,
        });
      });

    (sourceData.setorial?.allAssets || [])
      .filter(asset => ['buy', 'investigate', 'danger', 'watch'].includes(asset.signal))
      .forEach(asset => {
        const status = normalizedStatus(asset.signal);
        add({
          ticker: asset.ticker, name: asset.name, sector: asset.sectorName, price: asset.price,
          strategyKey: 'setorial', strategy: 'Análise Setorial', status: asset.signal,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score: null, context: `Z-Ativo ${fmtZ(asset.zScore)} · Gap ${asset.gap != null ? `${fmtN(asset.gap, 2)}σ` : '—'}`,
          portfolio: { refs: [{ label: 'Z do ativo', value: fmtZ(asset.zScore), highlight: true }, { label: 'Gap do setor', value: asset.gap != null ? `${fmtN(asset.gap, 2)}σ` : '—' }], entryPrice: asset.price },
          priority: asset.signal === 'buy' ? 0 : asset.signal === 'investigate' ? 1 : asset.signal === 'watch' ? 2 : 3,
        });
      });
    return rows;
  }, [data, sourceData, strategyHealth]);

  const filtered = useMemo(() => {
    let rows = [...consolidatedRows];
    if (strategyFilter !== 'all') rows = rows.filter(row => row.strategyKey === strategyFilter);
    if (filter === 'entry') rows = rows.filter(row => row.group === 'entry');
    if (filter === 'watch') rows = rows.filter(row => row.group === 'watch');
    if (filter === 'risk') rows = rows.filter(row => row.group === 'risk');
    if (tierFilter !== 'all') rows = rows.filter(row => (TIER_MAP[row.ticker] || 'B') === tierFilter);
    rows.sort((a, b) => {
      if (sortKey === 'priority') {
        const statusDifference = scannerStatusRank(a) - scannerStatusRank(b);
        if (statusDifference) return statusDifference;
        const leftRiskReward = Number(a.riskReward);
        const rightRiskReward = Number(b.riskReward);
        const riskDifference = (Number.isFinite(rightRiskReward) ? rightRiskReward : -Infinity)
          - (Number.isFinite(leftRiskReward) ? leftRiskReward : -Infinity);
        if (riskDifference) return riskDifference;
        return 0;
      }
      const values = {
        priority: [a.priority, b.priority],
        score: [a.score ?? -1, b.score ?? -1],
        ticker: [a.ticker, b.ticker],
        strategy: [a.strategy, b.strategy],
      };
      const [va, vb] = values[sortKey] || values.priority;
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return a.ticker.localeCompare(b.ticker);
    });
    return rows;
  }, [consolidatedRows, filter, strategyFilter, tierFilter, sortKey, sortAsc]);

  function toggleSort(key) {
    if (sortKey === key) setSortAsc(a => !a);
    else { setSortKey(key); setSortAsc(true); }
  }

  if (loading && !data) return (
    <div className="sc-root">
      <div className="sc-loading"><div className="sc-spinner" /><span>Carregando scanner…</span></div>
    </div>
  );
  if (error) return (
    <div className="sc-root">
      <div className="sc-error">⚠ {error} <button onClick={load}>Tentar novamente</button></div>
    </div>
  );
  if (!data) return null;

  const { kpis } = data;
  const entriesCount = consolidatedRows.filter(row => row.group === 'entry').length;
  const strategyCounts = Object.fromEntries(
    STRATEGIES.map(strategy => [strategy.key, consolidatedRows.filter(row => row.strategyKey === strategy.key).length])
  );
  const activeRefinementCount = Number(strategyFilter !== 'all') + Number(tierFilter !== 'all');

  return (
    <div className="sc-root">

      {newAlerts.length > 0 && (
        <div className="sc-alert-toast" aria-live="polite">
          {newAlerts.map((a, i) => (
            <DismissibleNotice
              key={`${a.ticker}-${a.type}-${a.eventKey || i}`}
              noticeKey={`scanner-signal-${a.ticker}-${a.type}`}
              eventKey={a.eventKey || `${a.ticker}:${a.type}:${a.label}`}
              className={`sc-alert-item sc-alert-${a.type}`}
              closeLabel={`Fechar alerta de ${a.ticker}`}
            >
              <span className="sc-alert-icon">{a.type === 'buy' ? '🟢' : a.type === 'sell' ? '🔴' : '🟡'}</span>
              <span className="sc-alert-ticker">{a.ticker}</span>
              <span className="sc-alert-label">{a.label}</span>
            </DismissibleNotice>
          ))}
        </div>
      )}

      {/* Consolidated strategy summary */}
      <div className="sc-kpis">
        <KpiCard label="Ocorrências" value={consolidatedRows.length} />
        <KpiCard label="Entradas" value={entriesCount} color={entriesCount > 0 ? 'green' : 'muted'} />
        <KpiCard label="Estratégias" value={STRATEGIES.filter(strategy => strategyCounts[strategy.key] > 0).length} color="green" />
        <KpiCard label="Avaliáveis hoje" value={kpis.total} sub={`de ${universeTickers.length || data.marketData?.universeSize || '—'} no universo`} />
      </div>
      {/* Controls */}
      <div className="sc-controls">
        <div className="sc-filters">
          {FILTERS.map(f => (
            <button key={f.key} className={`sc-filter-btn${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(f.key)}>
              {f.label}
              {f.key === 'entry' && entriesCount > 0 &&
                <span className="sc-filter-badge">{entriesCount}</span>
              }
            </button>
          ))}
        </div>
        <div className="sc-sorts">
          {SORTS.map(s => (
            <button key={s.key} className={`sc-sort-btn${sortKey === s.key ? ' active' : ''}`} onClick={() => toggleSort(s.key)}>
              {s.label} {sortKey === s.key ? (sortAsc ? '↑' : '↓') : ''}
            </button>
          ))}
        </div>
        <div className="sc-controls-right">
          <button
            className={`sc-refine-toggle${filtersOpen || activeRefinementCount ? ' active' : ''}`}
            onClick={() => setFiltersOpen(open => !open)}
            aria-expanded={filtersOpen}
            aria-controls="scanner-refinement-filters"
          >
            Filtros
            {activeRefinementCount > 0 && <span>{activeRefinementCount}</span>}
            <i>{filtersOpen ? '⌃' : '⌄'}</i>
          </button>
          <button className="sc-refresh-btn" onClick={load} disabled={loading}>↻ Atualizar</button>
        </div>
      </div>

      {filtersOpen && (
        <div id="scanner-refinement-filters" className="sc-refinement-panel">
          <div className="sc-filter-group">
            <span className="sc-filter-group-label">Origem do sinal</span>
            <div className="sc-strategy-options" aria-label="Filtrar por origem do sinal">
              <button className={`sc-strategy-filter${strategyFilter === 'all' ? ' active' : ''}`} onClick={() => setStrategyFilter('all')}>
                Todas <span>{consolidatedRows.length}</span>
              </button>
              {STRATEGIES.map(strategy => (
                <button
                  key={strategy.key}
                  className={`sc-strategy-filter ${strategy.tone}${strategyFilter === strategy.key ? ' active' : ''}`}
                  onClick={() => setStrategyFilter(strategy.key)}
                >
                  {strategy.label} <span>{strategyCounts[strategy.key]}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="sc-filter-group sc-risk-filter-group">
            <span className="sc-filter-group-label">Risco</span>
            <div className="sc-tier-options">
              <button className={`sc-tier-btn${tierFilter === 'all' ? ' active' : ''}`} onClick={() => setTierFilter('all')}>Todos</button>
              {Object.entries(TIER_META).map(([key, meta]) => (
                <button key={key} className={`sc-tier-btn ${meta.cls}${tierFilter === key ? ' active' : ''}`}
                  onClick={() => setTierFilter(tierFilter === key ? 'all' : key)} title={meta.desc}>
                  {meta.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="sc-table-wrap">
        <table className="sc-table">
          <thead>
            <tr>
              <th>Ativo</th>
              <th>Cotação</th>
              <th>Origem do sinal</th>
              <th>Setor</th>
              <th title="Beta calculado com retornos diários dos últimos 12 meses">Beta (1 ano)</th>
              <th>Contexto</th>
              <th onClick={() => toggleSort('score')} className="sc-th-sort">Score / progresso</th>
              <th>Status</th>
              <th aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {filtered.map(a => {
              const sectorColor = SECTOR_COLOR[a.sector] || '#4a5a75';
              const live = liveData[a.ticker];
              const price = live?.price ?? a.price;
              const strategy = STRATEGIES.find(item => item.key === a.strategyKey);
              return (
                <tr key={a.id}
                  className={`sc-row${a.group === 'entry' ? ' row-buy' : ''}${onAssetClick ? ' sc-row-clickable' : ''}`}
                  onClick={() => onAssetClick && onAssetClick(a.ticker, a.strategyKey === 'intraday' ? 'scanner' : a.strategyKey)}
                >
                  {/* Ativo */}
                  <td className="sc-td-ativo">
                    <div className="sc-ticker-row">
                      <span className="sc-ticker">{a.ticker}</span>
                      {TIER_MAP[a.ticker] && (
                        <span className={`sc-tier-badge ${TIER_META[TIER_MAP[a.ticker]]?.cls}`}>
                          {TIER_MAP[a.ticker]}
                        </span>
                      )}
                    </div>
                    <div className="sc-name">{(a.name || '').substring(0, 16)}</div>
                     {!a.needsReview && !a.entryBlocked ? (
                      <button
                        type="button"
                        className="sc-add-portfolio-btn"
                        onClick={event => { event.stopPropagation(); setPortfolioAsset(a); }}
                        disabled={a.entryBlocked}
                         title={undefined}
                      >
                        + Carteira
                      </button>
                    ) : (a.entryBlocked || a.requiresOverride) ? (
                      <span className="sc-blocked-signal" role="status">Sinal registrado, estratégia bloqueada</span>
                    ) : null}
                  </td>

                  <td className="sc-td-preco">
                    <span className={`sc-preco${flashTickers.has(a.ticker) ? ` sc-flash-${flashDir[a.ticker] || 'up'}` : ''}`}>{fmtPrice(price)}</span>
                    {live?.change != null && <span className={`sc-var ${live.change >= 0 ? 'up' : 'down'}`}>{live.change >= 0 ? '+' : ''}{live.change.toFixed(2).replace('.', ',')}%</span>}
                  </td>

                  <td>
                    <span className={`sc-strategy-badge ${strategy?.tone || ''}`}>{a.strategy}</span>
                  </td>

                  {/* Setor */}
                  <td className="sc-td-setor">
                    <div className="sc-sector-pill" style={{ background: `${sectorColor}18`, borderColor: `${sectorColor}35` }}>
                      <span className="sc-sector-icon">{SECTOR_EMOJI[a.sector] || '📈'}</span>
                      <span className="sc-sector-name">{a.sector}</span>
                    </div>
                  </td>

                  <td className="sc-td-beta" title="Beta mede a sensibilidade histórica do ativo; não indica compra ou venda.">
                    <div className="sc-beta-values">
                      <span>Setor <strong>{fmtBeta(a.betaSector)}</strong></span>
                      <span>IBOV <strong>{fmtBeta(a.betaIbovespa)}</strong></span>
                    </div>
                  </td>

                  <td className="sc-context">
                    {a.context}
                    {a.riskRewardBlocked && <span className="sc-risk-seal">R/R &lt; 1</span>}
                  </td>
                  <td><span className="sc-score-value">{a.score != null ? `${a.score}%` : '—'}</span></td>
                  <td><span className={`sc-signal ${a.statusTone}`}>{a.group === 'entry' && <span className="sc-signal-dot">●</span>}{a.statusLabel}</span></td>
                  <td className="sc-actions">
                    {!a.needsReview && !a.entryBlocked && !a.requiresOverride
                      ? <button type="button" className="sc-add-portfolio-btn" onClick={event => { event.stopPropagation(); setPortfolioAsset({ ...a, price }); }}>+ Carteira</button>
                      : (a.entryBlocked || a.requiresOverride) && <span className="sc-blocked-signal" role="status">Sinal registrado, estratégia bloqueada</span>}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="10" className="sc-empty-row">Nenhum setup encontrado para os filtros selecionados.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {portfolioAsset && (
        <PortfolioModal
          ticker={portfolioAsset.ticker}
          name={portfolioAsset.name || portfolioAsset.ticker}
          strategy={portfolioAsset.strategy}
          refs={[{ label: 'Cotação', value: fmtPrice(portfolioAsset.price) }, ...(portfolioAsset.portfolio?.refs || [])]}
          defaultEntryPrice={portfolioAsset.portfolio?.entryPrice ?? portfolioAsset.price}
          suggestedStop={portfolioAsset.portfolio?.stop}
          suggestedTarget={portfolioAsset.portfolio?.target}
          enforceMinRiskReward={portfolioAsset.strategyKey === 'reversal'}
          requiresOverride={portfolioAsset.requiresOverride}
          strategyHealth={portfolioAsset.strategyHealth}
          onClose={() => setPortfolioAsset(null)}
          onSave={async (formData) => {
            await addPosition({
              ticker: portfolioAsset.ticker,
              name: portfolioAsset.name || portfolioAsset.ticker,
              signal_score: portfolioAsset.portfolio?.signalScore ?? null,
              ...formData,
            });
            setPortfolioAsset(null);
          }}
        />
      )}
    </div>
  );
}
